import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { FaCalendarAlt, FaArrowRight, FaUser } from "react-icons/fa";

const API_URL = "https://goawin.xyz/";
const IMAGE_URL = "https://goawin.xyz/";

function BlogDetailed() {
  const { slug } = useParams();

  const [blog, setBlog] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSingleBlog = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}api/domain/blog/${slug}`, {
        withCredentials: true,
      });
      setBlog(res.data.data);
    } catch (err) {
      console.log("Server Error");
      setBlog(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlogs = async () => {
    try {
      const res = await axios.get(`${API_URL}api/domain/blogs`, {
        withCredentials: true,
      });
      setBlogs(res.data.data || []);
    } catch (err) {
      console.log("Server Error");
    }
  };

  useEffect(() => {
    if (slug) {
      fetchSingleBlog();
      fetchBlogs();
      window.scrollTo(0, 0);
    }
  }, [slug]);

  const getImage = (image) => {
    if (!image) return "/placeholder.jpg";
    if (image.startsWith("http")) return image;
    return `${IMAGE_URL}${image.replace(/\\/g, "/")}`;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fafafa] dark:bg-black">
        <h2 className="text-xl font-black text-zinc-900 dark:text-white md:text-2xl">
          Loading Blog...
        </h2>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fafafa] dark:bg-black">
        <h2 className="text-xl font-black text-zinc-900 dark:text-white md:text-2xl">
          Blog Not Found
        </h2>
      </div>
    );
  }

  const relatedBlogs = blogs.filter((item) => item.slug !== slug).slice(0, 3);
  const popularBlogs = blogs.filter((item) => item.slug !== slug).slice(0, 10);

  return (
    <>
      <Helmet>
        <title>{blog?.seo?.metaTitle}</title>
        <meta name="description" content={blog.seo.metaDescription} />
        <meta name="keywords" content={blog.seo.keywords} />
        <link
          rel="canonical"
          href={`https://webxsecure.in/blog/${blog.slug}`}
        />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.seo.metaDescription} />
        <meta
          property="og:image"
          content={`https://goawin.xyz/${blog.featuredImage}`}
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content={`https://webxsecure.in/blog/${blog.slug}`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.seo.metaDescription} />
        <meta
          name="twitter:image"
          content={`https://goawin.xyz/${blog.featuredImage}`}
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://webxsecure.in/blog/${blog.slug}`,
            },
            headline: blog.title,
            description: blog.seo.metaDescription,
            image: `https://goawin.xyz/${blog.featuredImage}`,
            datePublished: blog.createdAt,
            dateModified: blog.updatedAt,
            author: {
              "@type": "Person",
              name: blog.teamMember?.name || "Rohit Bhardwaj",
            },
            publisher: {
              "@type": "Organization",
              name: "Web X Secure",
              logo: {
                "@type": "ImageObject",
                url: "https://webxsecure.in/logo.png",
              },
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://webxsecure.in",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Blog",
                item: "https://webxsecure.in/blog",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: blog.title,
                item: `https://webxsecure.in/blog/${blog.slug}`,
              },
            ],
          })}
        </script>
        <meta property="article:published_time" content={blog.createdAt} />
        <meta property="article:modified_time" content={blog.updatedAt} />
        <meta property="article:author" content={blog.teamMember?.name} />
        <meta property="article:section" content={blog.category?.name} />
      </Helmet>

      <main className="bg-[#fafafa] text-zinc-900 dark:bg-black dark:text-white">
        {/* HERO */}
        <section className="relative overflow-hidden pt-24 pb-14 md:pt-32 md:pb-20">
          <div className="absolute left-0 top-0 h-[320px] w-[320px] rounded-full bg-primary/10 blur-[120px] md:h-[500px] md:w-[500px]" />
          <div className="absolute bottom-0 right-0 h-[320px] w-[320px] rounded-full bg-orange-300/10 blur-[120px] md:h-[500px] md:w-[500px]" />

          <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.05)_1px,transparent_1px)] bg-[size:55px_55px] dark:bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] md:bg-[size:70px_70px]" />

          <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-5">
            <span className="inline-flex rounded-full bg-primary/10 px-4 py-2 text-xs font-bold text-primary md:text-sm">
              {blog?.category?.name || "Web Development"}
            </span>

            <h1 className="mt-5 max-w-6xl text-3xl font-black leading-tight sm:text-4xl md:mt-6 md:text-6xl">
              {blog.title}
            </h1>

            <p className="mt-4 max-w-5xl text-sm leading-7 text-zinc-600 dark:text-zinc-400 md:mt-6 md:text-lg">
              {blog?.seo?.metaDescription}
            </p>

            <div className="mt-6 flex flex-wrap gap-4 text-xs text-zinc-600 dark:text-zinc-400 md:mt-8 md:gap-5 md:text-sm">
              <span className="flex items-center gap-2">
                <FaUser className="text-primary" />
                {blog?.teamMember?.name || "Admin"}
              </span>

              <span className="flex items-center gap-2">
                <FaCalendarAlt className="text-primary" />
                {new Date(blog.createdAt).toLocaleDateString("en-IN")}
              </span>
            </div>
          </div>
        </section>

        {/* CONTENT */}
        <section className="relative overflow-hidden bg-[#fafafa] pb-14 dark:bg-black md:pb-20">
          <div className="absolute left-0 top-0 h-[320px] w-[320px] rounded-full bg-primary/10 blur-[120px] md:h-[500px] md:w-[500px]" />
          <div className="absolute bottom-0 right-0 h-[320px] w-[320px] rounded-full bg-orange-300/10 blur-[120px] md:h-[500px] md:w-[500px]" />

          <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.05)_1px,transparent_1px)] bg-[size:55px_55px] dark:bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] md:bg-[size:70px_70px]" />

          <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-5">
            <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
              {/* MAIN BLOG */}
              <article className="min-w-0">
                <div className="rounded-2xl bg-white/80 p-4 shadow-sm backdrop-blur-xl dark:bg-white/[0.04] md:rounded-3xl md:p-8">
                  <img
                    src={getImage(blog.featuredImage)}
                    alt={blog.title}
                    className="mb-6 max-h-[520px] w-full rounded-2xl object-contain md:mb-8 md:rounded-3xl"
                  />

                  <div
                    className="jodit-content prose prose-sm max-w-none dark:prose-invert prose-headings:font-black prose-h2:text-2xl prose-h3:text-xl prose-a:text-primary prose-li:text-zinc-600 prose-p:text-zinc-600 dark:prose-li:text-zinc-400 dark:prose-p:text-zinc-400 md:prose-lg md:prose-h2:text-3xl md:prose-h3:text-2xl"
                    dangerouslySetInnerHTML={{ __html: blog.content }}
                  />

                  {blog?.seo?.keywords?.length > 0 && (
                    <div className="mt-8 flex flex-wrap gap-2 pt-5 md:mt-10 md:gap-3 md:pt-6">
                      {blog.seo.keywords.map((tag, index) => (
                        <span
                          key={index}
                          className="rounded-full bg-zinc-100 px-3 py-2 text-xs text-zinc-700 dark:bg-white/10 dark:text-zinc-300 md:px-4 md:text-sm"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* RELATED */}
                {relatedBlogs.length > 0 && (
                  <div className="mt-10 md:mt-14">
                    <h2 className="mb-5 text-2xl font-black md:mb-6 md:text-3xl">
                      Related Blogs You Might Like
                    </h2>

                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                      {relatedBlogs.map((item) => (
                        <Link
                          to={`/blog/${item.slug}`}
                          key={item._id}
                          className="rounded-2xl bg-white/80 p-4 shadow-sm backdrop-blur-xl transition hover:-translate-y-1 hover:shadow-xl dark:bg-white/[0.04] md:rounded-3xl"
                        >
                          <img
                            src={getImage(item.featuredImage)}
                            alt={item.title}
                            className="h-40 w-full rounded-2xl object-cover"
                          />

                          <h3 className="mt-4 line-clamp-2 font-bold leading-snug">
                            {item.title}
                          </h3>

                          <p className="mt-2 text-sm text-zinc-500">
                            {new Date(item.createdAt).toLocaleDateString(
                              "en-IN",
                            )}
                          </p>

                          <span className="mt-4 flex items-center gap-2 text-sm font-bold text-primary">
                            Read More <FaArrowRight />
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </article>

              {/* SIDEBAR */}
              <aside className="space-y-6 lg:sticky lg:top-28 lg:h-fit">
                <div className="rounded-2xl bg-white/80 p-5 shadow-sm backdrop-blur-xl dark:bg-white/[0.04] md:rounded-3xl md:p-6">
                  <p className="text-lg font-bold text-zinc-700 dark:text-zinc-200 md:text-xl">
                    {blog?.teamMember?.name || "Admin"}
                  </p>

                  <p className="mt-1 text-xs font-semibold text-zinc-400">
                    {blog?.teamMember?.post || "Author"}
                  </p>

                  <p className="mt-3 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                    {blog?.teamMember?.description}
                  </p>
                </div>

                {popularBlogs.length > 0 && (
                  <div className="rounded-2xl bg-white/80 p-5 shadow-sm backdrop-blur-xl dark:bg-white/[0.04] md:rounded-3xl md:p-6">
                    <h3 className="mb-5 text-lg font-black md:text-xl">
                      Popular Posts
                    </h3>

                    {popularBlogs.map((item) => (
                      <Link
                        to={`/blog/${item.slug}`}
                        key={item._id}
                        className="mb-4 flex gap-3 last:mb-0"
                      >
                        <img
                          src={getImage(item.featuredImage)}
                          alt={item.title}
                          className="h-16 w-20 shrink-0 rounded-xl object-cover"
                        />

                        <div className="min-w-0">
                          <h4 className="line-clamp-2 text-sm font-bold leading-snug hover:text-primary">
                            {item.title}
                          </h4>

                          <p className="mt-1 text-xs text-zinc-500">
                            {new Date(item.createdAt).toLocaleDateString(
                              "en-IN",
                            )}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                <div className="rounded-2xl bg-[#06111f]/95 p-5 text-white shadow-xl backdrop-blur-xl md:rounded-3xl md:p-6">
                  <h3 className="text-lg font-black md:text-xl">
                    Need Website Development?
                  </h3>

                  <p className="mt-3 text-sm text-zinc-300">
                    Let’s build a fast, secure and SEO-friendly website for your
                    business.
                  </p>

                  <a
                    href="https://webxsecure.in/contact"
                    target="_blank"
                    rel="noreferrer"
                    className="mt-6 inline-flex w-full justify-center rounded-xl bg-primary px-6 py-3 font-bold text-white transition hover:scale-105 sm:w-auto"
                  >
                    Book Free Call
                  </a>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default BlogDetailed;
